/**
 * Renders native Shopify bundle components from the Storefront API.
 *
 * The bundle definition (component variants + quantities) maintained in the
 * Shopify Bundles app is the single source of truth — no metafield involved.
 *
 * <bundle-components> renders the component rows (image, title, quantity,
 * subtitle and option selects) for the currently selected bundle variant.
 * <bundle-save> renders the "Save X%" badge computed from component prices.
 */
(() => {
  const API_VERSION = '2026-04';
  const COMPONENTS_QUERY = `
    query BundleComponents($handle: String!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      product(handle: $handle) {
        variants(first: 100) {
          nodes {
            id
            requiresComponents
            price { amount currencyCode }
            selectedOptions { name value }
            components(first: 30) {
              nodes {
                quantity
                productVariant {
                  id
                  title
                  price { amount }
                  image { url altText width height }
                  product {
                    title
                    handle
                    onlineStoreUrl
                    productType
                    featuredImage { url altText width height }
                    hidden: metafield(namespace: "seo", key: "hidden") { value }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
  const CARET_ICON =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>';

  const cache = new Map();

  // Persisted copy of the last API response so repeat views render
  // immediately without waiting for the network (avoids layout shift).
  const storageKey = (el) =>
    `bundle-components:${API_VERSION}:${el.dataset.handle}:${el.dataset.country}:${el.dataset.language}`;

  function readStoredVariants(el) {
    try {
      return JSON.parse(localStorage.getItem(storageKey(el)));
    } catch {
      return null;
    }
  }

  function writeStoredVariants(el, variants) {
    try {
      localStorage.setItem(storageKey(el), JSON.stringify(variants));
    } catch {
      // Storage full or unavailable — rendering still works from the network.
    }
  }

  // Last rendered markup per variant, hydrated by the inline script in
  // bundle-variant-selects.liquid before this file is even downloaded.
  // Key intentionally has no API version: it caches output, not API data.
  const htmlStorageKey = (el, variantId) =>
    `bundle-components-html:${el.dataset.handle}:${el.dataset.country}:${el.dataset.language}:${variantId}`;

  function fetchBundleVariants(el) {
    const { shop, token, handle, country, language } = el.dataset;
    if (!shop || !token || !handle) return Promise.resolve([]);
    const key = `${handle}:${country}:${language}`;
    if (!cache.has(key)) {
      cache.set(
        key,
        fetch(`https://${shop}/api/${API_VERSION}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query: COMPONENTS_QUERY,
            variables: {
              handle,
              country: country || null,
              language: language ? language.replace('-', '_').toUpperCase() : null,
            },
          }),
        })
          .then((response) => response.json())
          .then(({ data }) => (data && data.product ? data.product.variants.nodes : []))
          .catch(() => [])
      );
    }
    return cache.get(key);
  }

  const numericId = (gid) => gid.split('/').pop();
  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

  function imageUrl(url, height) {
    try {
      const parsed = new URL(url);
      parsed.searchParams.set('height', height);
      return parsed.toString();
    } catch {
      return url;
    }
  }

  // Mirrors the markup of the 'image' Liquid snippet (image_url: height + image_tag).
  function imageTag(image, fallbackAlt, displayHeight) {
    if (!image) return '';
    const width = image.width && image.height ? Math.round((displayHeight * image.width) / image.height) : null;
    const srcset = [1, 2, 3]
      .map((scale) => `${escapeHtml(imageUrl(image.url, displayHeight * scale))} ${scale}x`)
      .join(', ');
    return `<img
      src="${escapeHtml(imageUrl(image.url, displayHeight))}"
      srcset="${srcset}"
      alt="${escapeHtml(image.altText || fallbackAlt)}"
      ${width ? `width="${width}"` : ''}
      height="${displayHeight}"
      loading="lazy"
      style="object-fit: cover; height: 100%"
    >`;
  }

  function savingsPercentage(variant) {
    const components = variant.components.nodes;
    if (!components.length) return 0;
    const componentsPrice = components.reduce(
      (sum, component) => sum + parseFloat(component.productVariant.price.amount) * component.quantity,
      0
    );
    if (componentsPrice <= 0) return 0;
    return Math.round(((componentsPrice - parseFloat(variant.price.amount)) / componentsPrice) * 100);
  }

  function savingsBadge(variant, template) {
    const percentage = savingsPercentage(variant);
    if (percentage <= 0) return '';
    return `<span class="push-bundle__sale">${escapeHtml(template.replace('@@', percentage))}</span>`;
  }

  class BundleComponents extends HTMLElement {
    connectedCallback() {
      this.parentOptions = JSON.parse(this.dataset.options || '[]');
      this.componentOrder = JSON.parse(this.dataset.componentOrder || '[]');
      this.rowsContainer = this.querySelector('[data-bundle-rows]');
      this.addEventListener('change', this.onSelectChange.bind(this));

      // Until data is available, the static skeleton rows rendered by the
      // Liquid snippet keep the space reserved.
      const stored = readStoredVariants(this);
      if (stored && stored.length) this.setVariants(stored);

      fetchBundleVariants(this).then((variants) => {
        if (!variants.length) {
          this.clearSkeleton();
          return;
        }
        writeStoredVariants(this, variants);
        // Don't disturb a selection the user already made from cached data.
        if (!this.userInteracted) this.setVariants(variants);
        else this.variants = variants.filter((variant) => variant.requiresComponents);
      });
    }

    setVariants(variants) {
      this.variants = variants.filter((variant) => variant.requiresComponents);
      const initial =
        this.variants.find((variant) => numericId(variant.id) === this.dataset.variantId) ||
        this.variants[0];
      if (initial) this.renderVariant(initial);
      else this.clearSkeleton();
    }

    // The Storefront API only returns products published to the channel that
    // issued the token. When it returns nothing (product missing from that
    // channel, network error…), drop the placeholders instead of leaving
    // empty grey rows on the page forever.
    clearSkeleton() {
      if (!this.rowsContainer) return;
      this.rowsContainer
        .querySelectorAll('.product-bundle-component--skeleton')
        .forEach((row) => row.remove());
    }

    onSelectChange(event) {
      if (event.target.tagName !== 'SELECT' || !this.variants) return;
      this.userInteracted = true;
      const selected = {};
      this.querySelectorAll('select[name^="options["]').forEach((select) => {
        selected[select.name.slice(8, -1)] = select.value;
      });
      const variant = this.variants.find((candidate) =>
        candidate.selectedOptions.every((option) => selected[option.name] === option.value)
      );
      if (variant) this.renderVariant(variant);
    }

    renderVariant(variant) {
      if (!this.rowsContainer) return;
      // VariantSelects in global.js resolves the variant from the selects in
      // DOM order, so rows with a select must follow the option positions.
      // The API returns components in an arbitrary order; ties follow the
      // order curated in the bundle.component_variants metafield, then the
      // product title, whose numeric prefix is the catalog display order.
      const components = [...variant.components.nodes].sort((a, b) => {
        const positionDiff = this.optionPosition(a) - this.optionPosition(b);
        if (positionDiff !== 0) return positionDiff;
        const orderDiff = this.curatedPosition(a) - this.curatedPosition(b);
        if (orderDiff !== 0) return orderDiff;
        return a.productVariant.product.title.localeCompare(b.productVariant.product.title, undefined, {
          numeric: true,
        });
      });
      const html = components.map((component) => this.renderRow(component, variant)).join('');
      try {
        localStorage.setItem(htmlStorageKey(this, numericId(variant.id)), html);
      } catch {
        // Storage unavailable — skip the early-hydration cache.
      }
      if (html === this.rowsContainer.innerHTML) return;
      this.rowsContainer.innerHTML = html;
      document.dispatchEvent(
        new CustomEvent('bundle-components:variant-change', {
          detail: { handle: this.dataset.handle, variant },
        })
      );
    }

    renderRow(component, variant) {
      const componentVariant = component.productVariant;
      const product = componentVariant.product;
      const image = componentVariant.image || product.featuredImage;
      const productUrl =
        product.onlineStoreUrl || `${window.Shopify.routes.root}products/${product.handle}`;
      const isHidden = product.hidden && product.hidden.value === '1';
      const img = imageTag(image, product.title, 125);
      const imageWrapper = isHidden
        ? `<span class="product-bundle-component__image">${img}</span>`
        : `<a href="${escapeHtml(productUrl)}" class="product-bundle-component__image">${img}</a>`;

      const quantityBadge =
        component.quantity > 1
          ? ` <span class="product-bundle-component__quantity">&times;&nbsp;${component.quantity}</span>`
          : '';
      // The product type is the subtitle, same source as the title block on the
      // product page itself.
      const subtitle = product.productType;

      return `
        <div class="product-bundle-component">
          ${imageWrapper}
          <div class="product-bundle-component__content">
            <h2 class="product-bundle-component__title">${escapeHtml(product.title)}${quantityBadge}</h2>
            ${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ''}
            ${this.renderOption(component, variant)}
          </div>
        </div>
      `;
    }

    matchingOption(component) {
      const productTitle = component.productVariant.product.title.toLowerCase();
      return this.parentOptions.find(
        (candidate) => candidate.name !== 'Title' && candidate.name.toLowerCase().includes(productTitle)
      );
    }

    optionPosition(component) {
      const option = this.matchingOption(component);
      return option ? option.position : Number.MAX_SAFE_INTEGER;
    }

    curatedPosition(component) {
      const index = this.componentOrder.indexOf(component.productVariant.product.handle);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    }

    renderOption(component, variant) {
      const componentVariant = component.productVariant;
      const option = this.matchingOption(component);
      if (!option) {
        return componentVariant.title === 'Default Title'
          ? ''
          : `<p>${escapeHtml(componentVariant.title)}</p>`;
      }

      const selectedValue = (
        variant.selectedOptions.find((selectedOption) => selectedOption.name === option.name) || {}
      ).value;
      const selectId = `Option-${this.dataset.sectionId}-${option.position}`;
      const optionTags = option.values
        .map(
          (value) =>
            `<option value="${escapeHtml(value)}"${value === selectedValue ? ' selected="selected"' : ''}>${escapeHtml(value)}</option>`
        )
        .join('');

      return `
        <div class="product-form__input product-form__input--dropdown">
          <label class="visually-hidden" for="${escapeHtml(selectId)}">${escapeHtml(option.name)}</label>
          <div class="select">
            <select
              id="${escapeHtml(selectId)}"
              class="select__select"
              name="options[${escapeHtml(option.name)}]"
              form="${escapeHtml(this.dataset.productFormId || '')}"
            >${optionTags}</select>
            ${CARET_ICON}
          </div>
        </div>
      `;
    }
  }

  class BundleSave extends HTMLElement {
    connectedCallback() {
      this.onVariantChange = (event) => {
        if (event.detail.handle === this.dataset.handle) this.render(event.detail.variant);
      };
      document.addEventListener('bundle-components:variant-change', this.onVariantChange);

      const renderInitial = (variants) => {
        const variant =
          variants.find((candidate) => numericId(candidate.id) === this.dataset.variantId) || variants[0];
        if (variant) this.render(variant);
      };

      const stored = readStoredVariants(this);
      if (stored && stored.length) renderInitial(stored);
      fetchBundleVariants(this).then((variants) => {
        if (variants.length) renderInitial(variants);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('bundle-components:variant-change', this.onVariantChange);
    }

    render(variant) {
      let badge = savingsBadge(variant, this.dataset.saveTemplate || 'Save @@%');
      if (badge && this.dataset.hasText === 'true') {
        badge = `<br>${badge.replace('class="push-bundle__sale"', 'class="push-bundle__sale" style="margin-top: 1.1rem;"')}`;
      }
      this.innerHTML = badge;
    }
  }

  if (!customElements.get('bundle-components')) {
    customElements.define('bundle-components', BundleComponents);
  }
  if (!customElements.get('bundle-save')) {
    customElements.define('bundle-save', BundleSave);
  }
})();
